import React, {
    ReactElement, ReactNode, useMemo, useRef,
} from "react";
import {
    RiArrowUpDoubleLine, RiBookmarkLine, RiFlagLine, RiProgress3Line, RiSpeedLine, RiTempHotLine, RiTextSnippet,
} from "@remixicon/react";
import {
    AsyncObjectType, FormField, FormFieldType, FormFieldVisibility,
    useGetFormDataQuery,
} from "../../../__generated__/api";
import { ReplacementGroup } from "../../replacement-group";
import Select, { SelectInstanceHandler } from "../../select/Select";
import { CUSTOM_FIELD_PROPS, getFieldsForUserType, UserType } from "../field-type/FieldType";
import Icon from "../../theme/icon/Icon";

export interface SelectedGroup {
    field: FormField;
    group: ReplacementGroup;
}

const USER_REPLACEMENTS = [
    { field: "name", name: "Name" },
    { field: "firstName", name: "First Name" },
    { field: "lastName", name: "Last Name" },
    { field: "email", name: "Email" },
];

const DEFAULT_GROUP: ReplacementGroup = {
    id: "urn:fq:form-field:fields",
    description: "All custom fields not included in the ones above.",
    label: "Custom Fields",
};

const DEFAULT_SYSTEM_GROUP: ReplacementGroup = {
    id: "urn:fq:form-field:system-fields",
    description: "All custom fields not included in the ones above.",
    label: "System Fields",
};

const DEFAULT_FIELD_TYPE_GROUP: ReplacementGroup = {
    id: "urn:fq:form-field:field-fields",
    description: "All field types.",
    label: "New custom field",
};

export const REQUESTER_TEXT = "REQUESTER_TEXT";
export const AGENT_TEXT = "AGENT_TEXT";

const DEFAULT_TEXT_GROUP: ReplacementGroup = {
    id: "urn:fq:form-field:text-fields",
    description: "Text Fields.",
    label: "Text",
    input: [
        {
            id: REQUESTER_TEXT,
            isSystemField: true,
            name: "Requester Text",
            type: FormFieldType.Text,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: AGENT_TEXT,
            isSystemField: true,
            name: "Agent Text",
            type: FormFieldType.Text,
            visibility: FormFieldVisibility.Reusable,
        },
    ],
};

const CUSTOM_ICONS: Record<string, ReactNode> = {
    "requester-text": <RiTextSnippet />,
    "agent-text": <RiTextSnippet />,
    "urn:fq:form-field:status": <RiProgress3Line />,
    "urn:fq:form-field:priority": <RiFlagLine />,
    "urn:fq:form-field:severity": <RiTempHotLine />,
    "urn:fq:form-field:queueId": <RiSpeedLine />,
    "urn:fq:form-field:tags": <RiBookmarkLine />,
};

const EXCLUDE_SYSTEM_FIELDS = new Set([
    "urn:fq:form-field:status",
    "urn:fq:form-field:ticketNumberId",
    "urn:fq:form-field:id",
]);

export function useFlatFields(
    input: ReplacementGroup[] | undefined,
    {
        shouldShowAllFields,
        shouldGroupSystem,
        fieldDisplayUserType,
        shouldIncludeTextTypes,
    }: {
        shouldShowAllFields: boolean | undefined;
        shouldGroupSystem?: boolean;
        fieldDisplayUserType?: UserType;
        shouldIncludeTextTypes?: boolean;
    },
) {
    const { data: formData } = useGetFormDataQuery({
        variables: {
            shouldIncludeTemplateFields: false,
        },
    });

    return useMemo(() => {
        let allInput = [];

        const shouldGroup = shouldGroupSystem || fieldDisplayUserType || shouldIncludeTextTypes || shouldShowAllFields;

        if (shouldGroup) {
            const current = new Set((input ?? []).flatMap(row => row.input?.map(({ id }) => id)));
            if (fieldDisplayUserType) {
                const fields = getFieldsForUserType(fieldDisplayUserType);
                allInput.push({
                    ...DEFAULT_FIELD_TYPE_GROUP,
                    input: fields.map(field => ({
                        id: field.value,
                        isSystemField: true,
                        name: field.label,
                        type: field.value as FormFieldType,
                        visibility: FormFieldVisibility.Reusable,
                    } as FormField)),
                });
            }
            if (shouldIncludeTextTypes) {
                allInput.push(DEFAULT_TEXT_GROUP);
            }
            if (shouldShowAllFields) {
                if (shouldGroupSystem) {
                    if (input) {
                        const customInput = input.mapFilter(row => {
                            const fields = row.input?.filter(field => !field.isSystemField);
                            return fields?.isNotEmpty ? {
                                ...row,
                                input: fields,
                            } : undefined;
                        });
                        allInput = [...customInput, ...allInput];
                    }
                    allInput.push({
                        ...DEFAULT_SYSTEM_GROUP,
                        input: formData?.formFields?.filter(row => !EXCLUDE_SYSTEM_FIELDS.has(row.id)
                            && !current.has(row.id)
                            && row.isSystemField),
                    });
                    allInput.push({
                        ...DEFAULT_GROUP,
                        input: formData?.formFields?.filter(row => !row.isSystemField
                            && !current.has(row.id)
                            && row.visibility === FormFieldVisibility.Reusable),
                    });
                } else {
                    allInput.push({
                        ...DEFAULT_GROUP,
                        input: formData?.formFields?.filter(row => !current.has(row.id)),
                    });
                }
            }
        } else {
            allInput = input ? [...input] : [];
        }

        const flatFields = allInput.flatMap(group => group.input?.map(field => ({
            id: field.id,
            field,
            group,
        }))).filterNull().toIdMap("id");

        return {
            flatFields,
            input: allInput,
        };
    }, [input, formData]);
}
export default function FieldSelector({
    input, value, onChange, shouldExpandUsers, shouldShowAllFields, isClearable,
    shouldGroupSystem, fieldDisplayUserType, shouldIncludeTextTypes, shouldIncludeIcons,
    shouldAutoFocus,
}: {
    input?: ReplacementGroup[];
    value: SelectedGroup | string | undefined;
    onChange: (value: SelectedGroup | undefined) => void;
    shouldExpandUsers?: boolean;
    shouldShowAllFields?: boolean;
    shouldGroupSystem?: boolean;
    fieldDisplayUserType?: UserType;
    shouldIncludeTextTypes?: boolean;
    shouldIncludeIcons?: boolean;
    isClearable?: boolean;
    shouldAutoFocus?: boolean;
}): ReactElement {
    const { flatFields, input: allInput } = useFlatFields(input, {
        shouldShowAllFields,
        shouldGroupSystem,
        fieldDisplayUserType,
        shouldIncludeTextTypes,
    });

    const typedValue = typeof value === "string" ? flatFields?.[value] : value;

    const selectedCustomIcon = shouldIncludeIcons && typedValue
        ? CUSTOM_ICONS[typedValue.field.id as string] ?? CUSTOM_FIELD_PROPS[typedValue.field.type]?.icon
        : undefined;
    const options = useMemo(() => allInput?.mapFilter(row => {
        const children = row
            .input?.filter(val => val.asyncObjectType !== AsyncObjectType.FieldInContext
                    && val.asyncObjectType !== AsyncObjectType.FieldsInContext
                    && (shouldExpandUsers || !(val.id as string).startsWith("urn:fq:workflow-user-field:"))
                    && val.id !== "urn:fq:form-field:fields");

        return !children?.isNotEmpty ? undefined : {
            label: row.label,
            value: row.id,
            options: children?.flatMap(field => {
                const customField = shouldIncludeIcons ? CUSTOM_FIELD_PROPS[field.type] : undefined;
                return {
                    label: !shouldIncludeIcons ? field.name : (
                        <>
                            <Icon icon={CUSTOM_ICONS[field.id as string] ?? customField?.icon} className="me-2" />
                            {" "}
                            {field.name}
                        </>
                    ),
                    value: `${row.id}/${field.id}`,
                    data: {
                        field,
                        group: row,
                    } as SelectedGroup,
                };
            }),
        };
    }) ?? [], [shouldIncludeIcons, allInput, shouldExpandUsers]);
    return (
        <Select
            isClearable={isClearable}
            onChange={data => onChange(data?.data)}
            shouldAutoFocus={shouldAutoFocus}
            filterOption={(group, searchInput) => {
                const search = searchInput.toLocaleLowerCase();
                return group.group.label.toLocaleLowerCase().includes(search)
                    || group.group.description?.toLocaleLowerCase().includes(search)
                    || group.field.name.toLocaleLowerCase().includes(search)
                    || group.field.description?.toLocaleLowerCase().includes(search)
                    || false;
            }}
            value={!typedValue ? undefined : {
                label: !selectedCustomIcon ? typedValue.field.name : (
                    <>
                        <Icon icon={selectedCustomIcon} variant="default" className="me-2" />
                        {" "}
                        {typedValue.field.name}
                    </>
                ),
                value: `${typedValue.group.id}/${typedValue.field.id}`,
                data: typedValue,
            }}
            placeholder="Select field"
            options={options}
        />
    );
}

FieldSelector.defaultProps = {
    shouldExpandUsers: false,
    shouldShowAllFields: false,
    isClearable: false,
    shouldGroupSystem: false,
    fieldDisplayUserType: undefined,
    shouldIncludeTextTypes: false,
    input: undefined,
    shouldIncludeIcons: false,
    shouldAutoFocus: false,
};
